import React, { useState, useEffect, useContext } from 'react';
import {
  Alert,
  Button,
  Container,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams
} from 'react-router-dom';
import { Formik, Form, getIn } from 'formik';
import * as Yup from 'yup';

import logo from '../logo-trans.png';
import { useBodyClass, useLangParam } from '../hooks';
import MUI from '../mui';
import { ApiContext } from '../context/Api';
import { useTranslation } from 'react-i18next';
import LanguageSelect from '../components/LanguageSelect';

function ResetPassword() {
  useLangParam();
  const { apiService } = useContext(ApiContext);
  const navigate = useNavigate();
  const { t } = useTranslation();
  let { lang } = useParams();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useBodyClass('reset-password');
  useBodyClass('auth-screen');

  useEffect(() => {
    if (!searchParams.code) {
      navigate(`/${lang}/forgot-password`);
    }

    const user = JSON.parse(localStorage.getItem('userInfo'));
    if (user) {
      navigate(`/${lang}/`);
    }
  }, [navigate, searchParams, lang]);

  async function onReset(values) {
    setIsLoading(true);

    apiService
      .sendPostRequest('auth/reset-password', {
        code: searchParams.code,
        password: values.password,
        passwordConfirmation: values.confirmPassword
      })
      .then(() => {
        // Handle success.
        setIsLoading(false);
        setSuccess('Your password has been changed successfully');
        console.log("Your user's password has been changed.");
      })
      .catch(error => {
        // Handle error.
        setError(error.response.data.message[0].messages[0].message);
        setIsLoading(false);
        console.log('An error occurred:', error.response);
      });
  }
  return (
    <MUI mode="dark">
      <Container>
        <div className="top-lang">
          <LanguageSelect showLanguage />
        </div>
        <Stack
          direction="column"
          useFlexGap
          spacing={2}
          sx={{
            alignItems: 'center',
            justifyContent: 'center',
            marginBlockEnd: '1rem',
          }}
        >
          <img
            alt=""
            src={logo}
            width="80"
            className="d-inline-block align-top logo"
          />
          <Typography variant="h4">
            {t('core.progressingTogether')}
          </Typography>
          <Typography variant="h5">{t('auth.resetPassword')}</Typography>
        </Stack>
        <Formik
          initialValues={{ password: '', confirmPassword: '' }}
          validationSchema={Yup.object({
            password: Yup.string().required(t('core.required')),
            confirmPassword: Yup.string().required(t('core.required'))
          })}
          onSubmit={(values, { setSubmitting }) => {
            setError(null);
            setSuccess(null);
            onReset(values);
            setSubmitting(false);
          }}
        >
          {({ handleChange, errors, touched }) => (
            <Form className="form-base">
              <Stack direction="column" useFlexGap={true} spacing={2}>
                <TextField
                  label={t('auth.password') + '*'}
                  className="form-field"
                  type="password"
                  name="password"
                  variant="filled"
                  onChange={handleChange}
                  error={Boolean(
                    getIn(touched, 'password') &&
                      getIn(errors, 'password')
                  )}
                  helperText={
                    getIn(touched, 'password') &&
                    getIn(errors, 'password')
                      ? getIn(errors, 'password')
                      : ''
                  }
                />
                <TextField
                  label={t('auth.confirmPassword') + '*'}
                  className="form-field"
                  type="password"
                  name="confirmPassword"
                  variant="filled"
                  onChange={handleChange}
                  error={Boolean(
                    getIn(touched, 'confirmPassword') &&
                      getIn(errors, 'confirmPassword')
                  )}
                  helperText={
                    getIn(touched, 'confirmPassword') &&
                    getIn(errors, 'confirmPassword')
                      ? getIn(errors, 'confirmPassword')
                      : ''
                  }
                />

                {error && <Alert severity="error">{error}</Alert>}
                {success && <Alert severity="success">{success}</Alert>}

                <Button
                  type="submit"
                  variant="contained"
                  color="dark"
                  size="large"
                  loading={isLoading}
                  sx={{ width: '100%', fontSize: '1.25rem' }}
                >
                  {t('auth.resetPassword')}
                </Button>
                </Stack>
            </Form>
          )}
        </Formik>
        <div className="end-text text-center">
          {t('auth.alreadyHaveAccount')}&nbsp;
          <Link className="login-link-text" to="../signin">
            {t('auth.login')}
          </Link>
        </div>
      </Container>
    </MUI>
  );
}
export default ResetPassword;
